/**
 * File-handling callbacks for the infrastructure import modal.
 *
 * Extracted from ImportModal.tsx (Sub-PR 6.4).
 */

import { useCallback, useState } from 'react'

interface UseImportFileHandlersOptions {
  validate: (file: File) => Promise<unknown>
}

interface UseImportFileHandlersResult {
  selectedFile: File | null
  setSelectedFile: (f: File | null) => void
  dragActive: boolean
  handleFileSelect: (file: File) => Promise<void>
  handleDrag: (e: React.DragEvent) => void
  handleDrop: (e: React.DragEvent) => void
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void
}

function isImportable(file: File): boolean {
  return file.name.endsWith('.csv') || file.name.endsWith('.json')
}

export function useImportFileHandlers({
  validate,
}: UseImportFileHandlersOptions): UseImportFileHandlersResult {
  const [dragActive, setDragActive] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)

  const handleFileSelect = useCallback(
    async (file: File): Promise<void> => {
      setSelectedFile(file)
      await validate(file)
      return
    },
    [validate]
  )

  const handleDrag = useCallback((e: React.DragEvent): void => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent): void => {
      e.preventDefault()
      e.stopPropagation()
      setDragActive(false)
      const file = e.dataTransfer.files[0]
      if (file && isImportable(file)) {
        void handleFileSelect(file)
      }
    },
    [handleFileSelect]
  )

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      const file = e.target.files?.[0]
      if (file) void handleFileSelect(file)
    },
    [handleFileSelect]
  )

  return {
    selectedFile,
    setSelectedFile,
    dragActive,
    handleFileSelect,
    handleDrag,
    handleDrop,
    handleInputChange,
  }
}
